/* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema defaults are derived from heterogeneous internal definitions */
import type { ObjectSchema } from '../schemas/object'
import type * as types from './types'

type DefaultResult =
  | { hasDefault: false }
  | { hasDefault: true; value: unknown }

const noDefault: DefaultResult = { hasDefault: false }

/**
 * Materializes defaults declared within an object schema.
 *
 * Fields without defaults are omitted. Nested objects are included only when
 * at least one descendant has a default.
 */
export function getDefaults<T extends ObjectSchema<any>>(
  schema: T,
): Partial<types.output<T>> {
  const defaults = _getDefault(schema)
  return (defaults.hasDefault ? defaults.value : {}) as Partial<types.output<T>>
}

function _getDefault(schema: types.Schema): DefaultResult {
  const def = schema._zod.def

  if (def.type === 'default') {
    return {
      hasDefault: true,
      value:
        typeof def.defaultValue === 'function'
          ? def.defaultValue()
          : def.defaultValue,
    }
  }

  if (
    def.type === 'optional' ||
    def.type === 'nullable' ||
    def.type === 'catch'
  ) {
    return _getDefault(def.schema as types.Schema)
  }

  if (def.type !== 'object') return noDefault

  const defaults: Record<string, unknown> = {}
  let hasDefault = false

  const shape = def.shape as Record<string, types.Schema>
  for (const key in shape) {
    if (!Object.hasOwn(shape, key)) continue
    const value = _getDefault(shape[key])
    if (!value.hasDefault) continue
    defaults[key] = value.value
    hasDefault = true
  }

  return hasDefault ? { hasDefault: true, value: defaults } : noDefault
}
